import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Shield, Smartphone, Loader2, CheckCircle, QrCode, Key, Settings, RefreshCw, Trash2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

export const TwoFactorAuthModal = ({ 
  isOpen, 
  onClose, 
  onAuthenticated, 
  purpose = 'reports',
  title = "Double Authentification"
}) => {
  const [authMethod, setAuthMethod] = useState(null); // null, 'sms', 'totp'
  const [step, setStep] = useState('choose'); // 'choose', 'send', 'verify', 'setup-totp', 'complete', 'settings'
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [adminPhone, setAdminPhone] = useState('');
  const [newPhone, setNewPhone] = useState('');
  const [error, setError] = useState('');
  
  // TOTP (Google Authenticator)
  const [totpStatus, setTotpStatus] = useState({ is_configured: false, is_enabled: false });
  const [totpQrCode, setTotpQrCode] = useState('');
  const [totpSecret, setTotpSecret] = useState('');

  const token = localStorage.getItem('token');

  useEffect(() => {
    if (isOpen) {
      fetchAdminInfo();
      checkTotpStatus();
      setStep('choose');
      setAuthMethod(null);
      setCode('');
      setError('');
      setNewPhone('');
      setResetVerified(false);
      setResetMethod(null);
      setPassword('');
    }
  }, [isOpen]);

  // États pour la réinitialisation sécurisée
  const [resetVerified, setResetVerified] = useState(false);
  const [resetMethod, setResetMethod] = useState(null); // 'sms', 'totp', 'password'
  const [password, setPassword] = useState('');

  const fetchAdminInfo = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/auth/admin-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAdminPhone(response.data.admin_phone_full || response.data.admin_phone || '');
    } catch (err) {
      console.error('Error fetching admin info:', err);
    }
  };

  const checkTotpStatus = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/totp/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTotpStatus(response.data);
    } catch (err) {
      console.error('Error checking TOTP status:', err);
      setTotpStatus({ is_configured: false, is_enabled: false });
    }
  };

  // Envoyer code SMS pour vérification de réinitialisation
  const sendResetSmsCode = async () => {
    setLoading(true);
    setError('');
    try {
      await axios.post(`${BACKEND_URL}/api/auth/2fa/send-code`, {
        code_type: 'sms',
        purpose: 'settings_reset'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Code envoyé sur votre ancien numéro');
      setResetMethod('sms');
    } catch (err) {
      toast.error('Erreur envoi SMS');
      setError('Impossible d\'envoyer le SMS');
    } finally {
      setLoading(false);
    }
  };

  // Vérifier le code de réinitialisation
  const verifyResetCode = async () => {
    if (!code || code.length < 4) {
      setError('Code invalide');
      return;
    }
    
    setLoading(true);
    setError('');
    try {
      if (resetMethod === 'sms') {
        await axios.post(`${BACKEND_URL}/api/auth/2fa/verify-code`, {
          code: code,
          code_type: 'sms',
          purpose: 'settings_reset'
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else if (resetMethod === 'totp') {
        await axios.post(`${BACKEND_URL}/api/totp/verify`, {
          code: code
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      
      toast.success('Vérification réussie !');
      setResetVerified(true);
      setCode('');
    } catch (err) {
      setError('Code invalide');
      toast.error('Code incorrect');
    } finally {
      setLoading(false);
    }
  };

  // Vérifier le mot de passe
  const verifyPassword = async () => {
    if (!password) {
      setError('Entrez votre mot de passe');
      return;
    }
    
    setLoading(true);
    setError('');
    try {
      // Vérifier le mot de passe via l'endpoint login
      const userEmail = localStorage.getItem('userEmail');
      await axios.post(`${BACKEND_URL}/api/auth/login`, {
        email: userEmail,
        password: password
      });
      
      toast.success('Mot de passe vérifié !');
      setResetVerified(true);
      setPassword('');
    } catch (err) {
      setError('Mot de passe incorrect');
      toast.error('Mot de passe incorrect');
    } finally {
      setLoading(false);
    }
  };

  // Réinitialiser le numéro SMS (après vérification)
  const resetSmsNumber = async () => {
    if (!resetVerified) {
      toast.error('Vérification requise');
      return;
    }
    
    if (!newPhone || newPhone.length < 10) {
      toast.error('Veuillez entrer un numéro valide');
      return;
    }
    
    setLoading(true);
    try {
      let formattedPhone = newPhone.replace(/\s/g, '').replace(/\./g, '');
      if (formattedPhone.startsWith('0')) {
        formattedPhone = '+33' + formattedPhone.slice(1);
      }
      
      await axios.put(`${BACKEND_URL}/api/auth/admin-settings`, {
        admin_phone: formattedPhone,
        verification_code: 'reset_allowed'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Numéro SMS mis à jour !');
      setAdminPhone(newPhone);
      setNewPhone('');
      setResetVerified(false);
      setResetMethod(null);
      setStep('choose');
      fetchAdminInfo();
    } catch (err) {
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setLoading(false);
    }
  };

  // Réinitialiser Google Authenticator (après vérification)
  const resetTotp = async () => {
    if (!resetVerified) {
      toast.error('Vérification requise');
      return;
    }
    
    setLoading(true);
    try {
      // Utiliser le nouvel endpoint qui supprime l'ancien et génère un nouveau QR code
      const response = await axios.post(`${BACKEND_URL}/api/totp/reset`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Google Authenticator réinitialisé ! Scannez le nouveau QR code.');
      
      // Afficher le nouveau QR code
      setTotpQrCode(response.data.qr_code);
      setTotpSecret(response.data.manual_entry_key);
      setTotpStatus({ is_configured: false, is_enabled: false });
      setResetVerified(false);
      setResetMethod(null);
      setStep('setup-totp'); // Aller directement à la configuration avec le nouveau QR
    } catch (err) {
      toast.error('Erreur lors de la réinitialisation');
    } finally {
      setLoading(false);
    }
  };

  const setupTotp = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/totp/setup`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTotpQrCode(response.data.qr_code);
      setTotpSecret(response.data.manual_entry_key);
      setStep('setup-totp');
    } catch (err) {
      toast.error('Erreur lors de la configuration');
    } finally {
      setLoading(false);
    }
  };

  const selectSms = () => {
    setAuthMethod('sms');
    setStep('send');
  };

  const selectTotp = () => {
    setAuthMethod('totp');
    if (totpStatus.is_enabled) {
      setStep('verify');
    } else {
      setupTotp();
    }
  };

  const sendSmsCode = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/2fa/send-code`, {
        code_type: 'sms',
        purpose: purpose
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(response.data.message);
      setStep('verify');
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Erreur lors de l\'envoi du SMS';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const verifyCode = async () => {
    if (!code || code.length < 4) {
      toast.error('Veuillez entrer un code valide');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      if (authMethod === 'sms') {
        await axios.post(`${BACKEND_URL}/api/auth/2fa/verify-code`, {
          code: code,
          code_type: 'sms',
          purpose: purpose
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        // TOTP verification
        await axios.post(`${BACKEND_URL}/api/totp/verify`, {
          code: code
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      
      toast.success('Authentification réussie !');
      setStep('complete');
      
      setTimeout(() => {
        onAuthenticated();
      }, 1000);
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Code invalide';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-emerald-600" />
            {title}
          </DialogTitle>
        </DialogHeader>
        
        <div className="py-4">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}
          
          {/* Étape 1: Choisir la méthode */}
          {step === 'choose' && (
            <div className="space-y-4">
              <p className="text-center text-slate-600 mb-4">
                Choisissez votre méthode d'authentification :
              </p>
              
              {/* Option SMS */}
              <button
                onClick={selectSms}
                disabled={!adminPhone}
                className="w-full p-4 border-2 border-slate-200 rounded-xl hover:border-emerald-500 hover:bg-emerald-50 transition-all flex items-center gap-4 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center">
                  <Smartphone className="w-6 h-6 text-emerald-600" />
                </div>
                <div className="text-left flex-1">
                  <div className="font-semibold text-slate-900">SMS</div>
                  <div className="text-sm text-slate-500">
                    {adminPhone ? `Code envoyé à ****${adminPhone.slice(-4)}` : 'Numéro non configuré'}
                  </div>
                </div>
              </button>
              
              {/* Option Google Authenticator */}
              <button
                onClick={selectTotp}
                className="w-full p-4 border-2 border-slate-200 rounded-xl hover:border-purple-500 hover:bg-purple-50 transition-all flex items-center gap-4"
              >
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                  <QrCode className="w-6 h-6 text-purple-600" />
                </div>
                <div className="text-left flex-1">
                  <div className="font-semibold text-slate-900">Google Authenticator</div>
                  <div className="text-sm text-slate-500">
                    {totpStatus.is_enabled ? 'Configuré - Entrer le code' : 'Configurer avec QR code'}
                  </div>
                </div>
                {totpStatus.is_enabled && (
                  <CheckCircle className="w-5 h-5 text-green-500" />
                )}
              </button>
              
              {/* Lien vers les paramètres */}
              <div className="pt-4 border-t border-slate-200">
                <button
                  onClick={() => setStep('settings')}
                  className="w-full text-center text-sm text-slate-500 hover:text-slate-700 flex items-center justify-center gap-2"
                >
                  <Settings className="w-4 h-4" />
                  Changer de numéro ou réinitialiser Google Auth
                </button>
              </div>
            </div>
          )}
          
          {/* Paramètres de réinitialisation */}
          {step === 'settings' && (
            <div className="space-y-4">
              {!resetVerified ? (
                <>
                  {/* Étape 1: Vérification d'identité */}
                  <div className="text-center mb-4">
                    <Shield className="w-12 h-12 text-amber-500 mx-auto mb-2" />
                    <p className="text-slate-600 font-medium">
                      Vérification de sécurité requise
                    </p>
                    <p className="text-sm text-slate-500">
                      Pour modifier vos paramètres, confirmez votre identité :
                    </p>
                  </div>
                  
                  {!resetMethod && (
                    <div className="space-y-3">
                      {/* Option 1: SMS sur ancien numéro */}
                      <button
                        onClick={sendResetSmsCode}
                        disabled={loading}
                        className="w-full p-3 border-2 border-slate-200 rounded-xl hover:border-emerald-500 hover:bg-emerald-50 transition-all flex items-center gap-3"
                      >
                        <Smartphone className="w-5 h-5 text-emerald-600" />
                        <div className="text-left flex-1">
                          <div className="font-medium text-slate-900">Code SMS</div>
                          <div className="text-xs text-slate-500">Recevoir un code sur ****{adminPhone.slice(-4)}</div>
                        </div>
                      </button>
                      
                      {/* Option 2: Google Auth si configuré */}
                      {totpStatus.is_enabled && (
                        <button
                          onClick={() => setResetMethod('totp')}
                          className="w-full p-3 border-2 border-slate-200 rounded-xl hover:border-purple-500 hover:bg-purple-50 transition-all flex items-center gap-3"
                        >
                          <QrCode className="w-5 h-5 text-purple-600" />
                          <div className="text-left flex-1">
                            <div className="font-medium text-slate-900">Google Authenticator</div>
                            <div className="text-xs text-slate-500">Utiliser votre code à 6 chiffres</div>
                          </div>
                        </button>
                      )}
                      
                      {/* Option 3: Mot de passe */}
                      <button
                        onClick={() => setResetMethod('password')}
                        className="w-full p-3 border-2 border-slate-200 rounded-xl hover:border-slate-400 hover:bg-slate-50 transition-all flex items-center gap-3"
                      >
                        <Key className="w-5 h-5 text-slate-600" />
                        <div className="text-left flex-1">
                          <div className="font-medium text-slate-900">Mot de passe</div>
                          <div className="text-xs text-slate-500">Entrer le mot de passe du compte</div>
                        </div>
                      </button>
                    </div>
                  )}
                  
                  {/* Formulaire de vérification SMS ou TOTP */}
                  {(resetMethod === 'sms' || resetMethod === 'totp') && (
                    <div className="space-y-3">
                      <p className="text-center text-sm text-slate-600">
                        {resetMethod === 'sms' ? 'Entrez le code reçu par SMS :' : 'Entrez le code Google Authenticator :'}
                      </p>
                      <Input
                        type="text"
                        value={code}
                        onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        placeholder="000000"
                        className="text-center text-2xl tracking-widest font-mono"
                        maxLength={6}
                      />
                      {error && <p className="text-red-500 text-sm text-center">{error}</p>}
                      <Button 
                        onClick={verifyResetCode}
                        disabled={loading || code.length < 4}
                        className="w-full"
                      >
                        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Vérifier'}
                      </Button>
                      <Button 
                        variant="ghost"
                        onClick={() => { setResetMethod(null); setCode(''); setError(''); }}
                        className="w-full"
                      >
                        Autre méthode
                      </Button>
                    </div>
                  )}
                  
                  {/* Formulaire mot de passe */}
                  {resetMethod === 'password' && (
                    <div className="space-y-3">
                      <p className="text-center text-sm text-slate-600">
                        Entrez votre mot de passe :
                      </p>
                      <Input
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Votre mot de passe"
                        className="w-full"
                      />
                      {error && <p className="text-red-500 text-sm text-center">{error}</p>}
                      <Button 
                        onClick={verifyPassword}
                        disabled={loading || !password}
                        className="w-full"
                      >
                        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Vérifier'}
                      </Button>
                      <Button 
                        variant="ghost"
                        onClick={() => { setResetMethod(null); setPassword(''); setError(''); }}
                        className="w-full"
                      >
                        Autre méthode
                      </Button>
                    </div>
                  )}
                </>
              ) : (
                <>
                  {/* Étape 2: Modification après vérification */}
                  <div className="text-center mb-4">
                    <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-2" />
                    <p className="text-green-600 font-medium">
                      Identité vérifiée !
                    </p>
                    <p className="text-sm text-slate-500">
                      Vous pouvez maintenant modifier vos paramètres :
                    </p>
                  </div>
                  
                  {/* Changer le numéro SMS */}
                  <div className="p-4 border-2 border-slate-200 rounded-xl space-y-3">
                    <div className="flex items-center gap-3">
                      <Smartphone className="w-5 h-5 text-emerald-600" />
                      <span className="font-semibold text-slate-900">Changer le numéro SMS</span>
                    </div>
                    <p className="text-sm text-slate-500">
                      Actuel : {adminPhone ? `****${adminPhone.slice(-4)}` : 'Non configuré'}
                    </p>
                    <Input
                      type="tel"
                      value={newPhone}
                      onChange={(e) => setNewPhone(e.target.value)}
                      placeholder="Nouveau numéro (ex: 0612345678)"
                      className="w-full"
                    />
                    <Button 
                      onClick={resetSmsNumber}
                      disabled={loading || !newPhone}
                      size="sm"
                      className="w-full bg-emerald-600 hover:bg-emerald-700"
                    >
                      {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                        <>
                          <RefreshCw className="w-4 h-4 mr-2" />
                          Mettre à jour le numéro
                        </>
                      )}
                    </Button>
                  </div>
                  
                  {/* Réinitialiser Google Authenticator */}
                  <div className="p-4 border-2 border-slate-200 rounded-xl space-y-3">
                    <div className="flex items-center gap-3">
                      <QrCode className="w-5 h-5 text-purple-600" />
                      <span className="font-semibold text-slate-900">Google Authenticator</span>
                    </div>
                    <p className="text-sm text-slate-500">
                      {totpStatus.is_enabled 
                        ? "Configuré et actif - Cliquez pour générer un nouveau QR code"
                        : "Non configuré - Cliquez pour générer un QR code"}
                    </p>
                    <Button 
                      onClick={resetTotp}
                      disabled={loading}
                      size="sm"
                      className={totpStatus.is_enabled 
                        ? "w-full bg-amber-600 hover:bg-amber-700" 
                        : "w-full bg-purple-600 hover:bg-purple-700"}
                    >
                      {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                        <>
                          <RefreshCw className="w-4 h-4 mr-2" />
                          {totpStatus.is_enabled 
                            ? "Régénérer nouveau QR Code" 
                            : "Générer QR Code"}
                        </>
                      )}
                    </Button>
                    {totpStatus.is_enabled && (
                      <p className="text-xs text-amber-600 text-center">
                        ⚠️ L'ancien QR code sera invalidé
                      </p>
                    )}
                  </div>
                </>
              )}
              
              <Button 
                variant="outline"
                onClick={() => { 
                  setStep('choose'); 
                  setResetVerified(false); 
                  setResetMethod(null);
                  setCode('');
                  setPassword('');
                  setError('');
                }}
                className="w-full"
              >
                Retour
              </Button>
            </div>
          )}
          
          {/* Étape SMS: Envoyer le code */}
          {step === 'send' && authMethod === 'sms' && (
            <div className="space-y-4">
              <div className="text-center p-4 bg-slate-50 rounded-lg">
                <Smartphone className="w-12 h-12 text-emerald-600 mx-auto mb-3" />
                <p className="text-slate-700 mb-2">Un code de vérification sera envoyé par SMS à :</p>
                <p className="font-bold text-lg text-slate-900">
                  {adminPhone ? `****${adminPhone.slice(-4)}` : 'Numéro non configuré'}
                </p>
              </div>
              
              <div className="flex gap-2">
                <Button 
                  variant="outline"
                  onClick={() => setStep('choose')}
                  className="flex-1"
                >
                  Retour
                </Button>
                <Button 
                  onClick={sendSmsCode} 
                  disabled={loading || !adminPhone}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    'Envoyer SMS'
                  )}
                </Button>
              </div>
            </div>
          )}
          
          {/* Étape TOTP: Configuration avec QR code */}
          {step === 'setup-totp' && (
            <div className="space-y-4">
              <div className="text-center">
                <p className="text-slate-700 mb-4">
                  Scannez ce QR code avec Google Authenticator :
                </p>
                
                {totpQrCode && (
                  <div className="flex justify-center mb-4">
                    <img 
                      src={totpQrCode} 
                      alt="QR Code Google Authenticator" 
                      className="w-48 h-48 border-2 border-slate-200 rounded-lg"
                    />
                  </div>
                )}
                
                <div className="bg-slate-100 rounded-lg p-3 mb-4">
                  <p className="text-xs text-slate-500 mb-1">Clé manuelle :</p>
                  <code className="text-sm font-mono text-slate-800 break-all">{totpSecret}</code>
                </div>
                
                <p className="text-sm text-slate-600 mb-4">
                  Après avoir scanné, entrez le code à 6 chiffres :
                </p>
                
                <Input
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  className="text-center text-2xl tracking-widest font-mono mb-4"
                  maxLength={6}
                />
                
                <div className="flex gap-2">
                  <Button 
                    variant="outline"
                    onClick={() => setStep('choose')}
                    className="flex-1"
                  >
                    Retour
                  </Button>
                  <Button 
                    onClick={verifyCode}
                    disabled={loading || code.length < 6}
                    className="flex-1 bg-purple-600 hover:bg-purple-700"
                  >
                    {loading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      'Activer'
                    )}
                  </Button>
                </div>
              </div>
            </div>
          )}
          
          {/* Étape: Vérifier le code */}
          {step === 'verify' && (
            <div className="space-y-4">
              <div className="text-center mb-4">
                {authMethod === 'sms' ? (
                  <>
                    <Smartphone className="w-10 h-10 text-emerald-600 mx-auto mb-2" />
                    <p className="text-slate-600">Entrez le code reçu par SMS</p>
                  </>
                ) : (
                  <>
                    <Key className="w-10 h-10 text-purple-600 mx-auto mb-2" />
                    <p className="text-slate-600">Entrez le code Google Authenticator</p>
                  </>
                )}
              </div>
              
              <Input
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                className="text-center text-2xl tracking-widest font-mono"
                maxLength={6}
                autoFocus
              />
              
              <div className="flex gap-2">
                {authMethod === 'sms' && (
                  <Button 
                    variant="outline" 
                    onClick={() => setStep('send')}
                    className="flex-1"
                  >
                    Renvoyer
                  </Button>
                )}
                <Button 
                  variant="outline"
                  onClick={() => setStep('choose')}
                  className={authMethod === 'sms' ? '' : 'flex-1'}
                >
                  Retour
                </Button>
                <Button 
                  onClick={verifyCode}
                  disabled={loading || code.length < 4}
                  className={`flex-1 ${authMethod === 'sms' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-purple-600 hover:bg-purple-700'}`}
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    'Vérifier'
                  )}
                </Button>
              </div>
              
              {/* Lien pour réinitialiser si clé perdue - seulement pour TOTP */}
              {authMethod === 'totp' && (
                <div className="pt-4 border-t border-slate-200 mt-4">
                  <button
                    onClick={() => setStep('settings')}
                    className="w-full text-center text-sm text-amber-600 hover:text-amber-700 flex items-center justify-center gap-2"
                  >
                    <RefreshCw className="w-4 h-4" />
                    J'ai perdu ma clé - Réinitialiser via SMS ou mot de passe
                  </button>
                </div>
              )}
            </div>
          )}
          
          {/* Étape finale: Succès */}
          {step === 'complete' && (
            <div className="text-center py-8">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <p className="text-lg font-semibold text-green-700">Authentification réussie !</p>
              <p className="text-slate-500 mt-2">Accès autorisé aux rapports</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TwoFactorAuthModal;
